import React from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { adminApi } from '../../../lib/adminAuth';
import { Field } from './ContractDetailsForm';
import EmailChips from './EmailChips';
import ListEditor from './ListEditor';
import { errMsg } from './docUtils';

const TEXT = [['company_legal_name', 'Company legal name'], ['company_short', 'Short name used in clauses'], ['company_gstin', 'GSTIN'],
  ['company_city', 'City, State'], ['company_website', 'Website'], ['signatory_name', 'Signatory name'], ['signatory_designation', 'Signatory designation'],
  ['signatory_email', 'Signatory email'], ['jurisdiction_city', 'Courts city (jurisdiction)'], ['contract_title', 'Contract title']];
const NUMS = [['invoice_day', 'Invoice raised on day'], ['payment_due_day', 'Payment due by day'], ['notice_days', 'Notice period (days)'],
  ['credit_months', 'Credit limit (months)'], ['proposal_validity_days', 'Proposal valid for (days)']];

export default function LibrarySettings({ settings, canEdit, reload }) {
  const [f, setF] = React.useState(settings);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => setF(settings), [settings]);
  const d = !canEdit;
  const save = async () => {
    setBusy(true);
    try { await adminApi.put('/api/admin/scope-library/settings', f); toast.success('Library settings saved'); reload(); }
    catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="library-settings">
      <section>
        <p className="text-sm font-semibold text-slate-800 mb-2">Company details</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {TEXT.map(([k, l]) => <Field key={k} label={l}><Input value={f[k] || ''} disabled={d} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`lib-${k}`} /></Field>)}
          <Field label="Registered office address"><Textarea rows={2} value={f.company_address || ''} disabled={d} onChange={(e) => setF({ ...f, company_address: e.target.value })} data-testid="lib-company_address" /></Field>
        </div>
      </section>
      <section>
        <p className="text-sm font-semibold text-slate-800 mb-2">Default terms</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {NUMS.map(([k, l]) => <Field key={k} label={l}><Input type="number" min="1" value={f[k] ?? ''} disabled={d} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} data-testid={`lib-${k}`} /></Field>)}
        </div>
        <label className="flex items-center gap-2 text-sm mt-3"><Switch checked={!!f.auto_draft_on_client_create} disabled={d} onCheckedChange={(v) => setF({ ...f, auto_draft_on_client_create: v })} data-testid="lib-auto-draft" /> Draft a contract automatically when a client is added</label>
      </section>
      <section>
        <p className="text-sm font-semibold text-slate-800 mb-2">Email</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Default CC on every proposal and contract"><EmailChips value={f.default_cc || []} onChange={(v) => setF({ ...f, default_cc: v })} testId="lib-default-cc" /></Field>
          <Field label="Default sender mailbox" hint="Must be connected in Settings → Email"><Input value={f.default_sender || ''} disabled={d} onChange={(e) => setF({ ...f, default_sender: e.target.value })} data-testid="lib-default_sender" /></Field>
        </div>
      </section>
      <section className="space-y-3">
        <p className="text-sm font-semibold text-slate-800">Schedule A defaults</p>
        <Field label="How we operate (SOP points)"><ListEditor value={f.sop_points || []} disabled={d} onChange={(v) => setF({ ...f, sop_points: v })} testId="lib-sop" /></Field>
        <Field label="Note under the SOP"><Textarea rows={2} value={f.sop_note || ''} disabled={d} onChange={(e) => setF({ ...f, sop_note: e.target.value })} data-testid="lib-sop-note" /></Field>
        <Field label="Team on the account"><Textarea rows={2} value={f.team_text || ''} disabled={d} onChange={(e) => setF({ ...f, team_text: e.target.value })} data-testid="lib-team" /></Field>
        <Field label="Exclusions"><Textarea rows={2} value={f.exclusions_text || ''} disabled={d} onChange={(e) => setF({ ...f, exclusions_text: e.target.value })} data-testid="lib-exclusions" /></Field>
      </section>
      <section className="space-y-3">
        <p className="text-sm font-semibold text-slate-800">Proposal defaults</p>
        <Field label="Introduction" hint="{{brand}} is replaced with the brand name"><Textarea rows={3} value={f.proposal_intro || ''} disabled={d} onChange={(e) => setF({ ...f, proposal_intro: e.target.value })} data-testid="lib-proposal-intro" /></Field>
        <Field label="Key terms"><ListEditor value={f.proposal_terms || []} disabled={d} onChange={(v) => setF({ ...f, proposal_terms: v })} addLabel="Add term" testId="lib-proposal-terms" /></Field>
      </section>
      {canEdit && (
        <div className="sticky bottom-0 bg-white/90 backdrop-blur py-3 border-t border-slate-100">
          <Button onClick={save} disabled={busy} className="bg-gray-900 hover:bg-gray-800" data-testid="lib-settings-save">{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save settings</Button>
        </div>
      )}
    </div>
  );
}
